import {useEffect, useMemo, useRef} from "react";
import {alpha, Box, Button, IconButton, Stack, ThemeProvider} from "@mui/material";
import {CloseOutlined} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import {useTranslation} from "react-i18next";
import {Link as RouterLink, useLocation} from "react-router-dom";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import MarkdownWrapper from "../../shared/markdown-wrapper/MarkdownWrapper";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import {RoutePaths, StorageConstants} from "../../../util/constants";
import {togglePromoModal} from "../../../store/app/Slice";
import useHasOpenDialog from "../../../hooks/useHasOpenDialog";

const PROMO_MODAL_DELAY_MS = 3000

const PromoDialog = () => {
    const dispatch = useAppDispatch()
    const {i18n, t} = useTranslation()
    const {pathname} = useLocation()
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const open = useAppSelector((state: RootState) => state.appReducer.promoModalOpen)
    const wheelOpen = useAppSelector((state: RootState) => state.appReducer.spinTheWheelOpen)
    const homeLoading = useAppSelector((state: RootState) => state.homeReducer.loading)
    const promo = useAppSelector((state: RootState) => state.homeReducer.content.promo_modal)
    const hasBlockingDialog = useHasOpenDialog(["promo-dialog"])

    const promoSignature = useMemo(() => JSON.stringify({
        image: promo?.image ?? "",
        link: promo?.link ?? "",
        title_en: promo?.title_en ?? "",
        title_mk: promo?.title_mk ?? "",
        title_al: promo?.title_al ?? "",
        subtitle_en: promo?.subtitle_en ?? "",
        subtitle_mk: promo?.subtitle_mk ?? "",
        subtitle_al: promo?.subtitle_al ?? "",
    }), [promo])

    const localizedTitle = promo?.[`title_${i18n.language}`] ?? ""
    const localizedSubtitle = promo?.[`subtitle_${i18n.language}`] ?? ""
    const promoLink = promo?.link?.trim() ?? ""
    const hasPromoContent = Boolean(promo?.image && localizedTitle.trim() && localizedSubtitle.trim())
    const dismissedSignature = localStorage.getItem(StorageConstants.HOME_PROMO_DISMISSED)
    const isDismissed = dismissedSignature === promoSignature
    const canShowPromo = pathname === RoutePaths.HOME && !homeLoading && hasPromoContent && !isDismissed
    const isExternalLink = /^https?:\/\//i.test(promoLink)

    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }

        if (!canShowPromo) {
            if (open) {
                dispatch(togglePromoModal(false))
            }
            return
        }

        if (wheelOpen || open || hasBlockingDialog) {
            return
        }

        timerRef.current = setTimeout(() => {
            dispatch(togglePromoModal(true))
        }, PROMO_MODAL_DELAY_MS)

        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [canShowPromo, dispatch, hasBlockingDialog, open, wheelOpen])

    const handleClose = () => {
        localStorage.setItem(StorageConstants.HOME_PROMO_DISMISSED, promoSignature)
        dispatch(togglePromoModal(false))
    }

    const handlePromoLinkClick = () => {
        localStorage.setItem(StorageConstants.HOME_PROMO_DISMISSED, promoSignature)
        dispatch(togglePromoModal(false))
    }

    if (!canShowPromo) {
        return null
    }

    const dialogContent = (
        <Box position="relative">
            <Box position="relative">
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={(theme) => ({
                        position: "absolute",
                        top: {xs: 10, sm: 12},
                        right: {xs: 10, sm: 12},
                        zIndex: 2,
                        width: {xs: 46, sm: 50},
                        height: {xs: 46, sm: 50},
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.36),
                        backdropFilter: "blur(6px)",
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                        "&:hover": {
                            backgroundColor: alpha(theme.palette.secondary.main, 0.5),
                        }
                    })}
                >
                    <CloseOutlined sx={{fontSize: {xs: "2rem", sm: "2.15rem"}}}/>
                </IconButton>

                <Box position="relative" sx={{borderRadius: 1.5, overflow: "hidden"}}>
                    <Box
                        component="img"
                        src={promo.image}
                        alt={localizedTitle}
                        sx={{
                            width: "100%",
                            minHeight: {xs: 230, sm: 280},
                            maxHeight: {xs: 320, sm: 360},
                            objectFit: "cover",
                            display: "block"
                        }}
                    />
                    <Box
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(180deg, rgba(7,6,7,0.06) 0%, rgba(7,6,7,0.14) 26%, rgba(7,6,7,0.68) 70%, rgba(7,6,7,0.94) 100%)"
                        }}
                    />
                    <Stack
                        spacing={0.75}
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                            px: {xs: 2, sm: 2.75},
                            pb: {xs: 1.75, sm: 2.2},
                        }}
                    >
                        <Typography
                            variant="h3"
                            color="primary"
                            fontWeight={700}
                            sx={{
                                fontSize: {xs: "1.56rem", sm: "1.98rem"},
                                lineHeight: 1,
                            }}
                        >
                            {localizedTitle}
                        </Typography>
                    </Stack>
                </Box>
            </Box>

            <Stack
                spacing={1.5}
                sx={{
                    px: 0,
                    pt: {xs: 1.5, sm: 1.85},
                }}
            >
                <MarkdownWrapper
                    source={localizedSubtitle}
                />
                {promoLink ? (
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        onClick={handlePromoLinkClick}
                        component={isExternalLink ? "a" : RouterLink}
                        href={isExternalLink ? promoLink : undefined}
                        to={isExternalLink ? undefined : promoLink}
                        sx={{alignSelf: "flex-start", mt: 0.25}}
                    >
                        {t("explore")}
                    </Button>
                ) : null}
            </Stack>
        </Box>
    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog
                open={open && !hasBlockingDialog}
                maxWidth="sm"
                dialogId="promo-dialog"
                handleDialogClose={handleClose}
                content={dialogContent}
                contentSx={{p: 0}}
            />
        </ThemeProvider>
    )
}

export default PromoDialog
